import type { AuthUser, OwnRoleModule } from './auth-provider'
import { coreUrl } from '@/shared/lib/api/api-base'
import { ACCESS_SUSPENDED_CODE } from './access-suspended'

interface UserIdentityResponse {
  UserId: number
  TenantId: number
  Username: string | null
  Firstname: string | null
  Lastname: string | null
  Fullname: string | null
  Email: string | null
  IsAdmin: boolean
  /**
   * Platform-tier super admin flag (TH-457). Assigned only from `th_admin`;
   * nobody inside a tenant can grant it. The confirmed contract is a non-null
   * `bool`, but the field is optional here because `th_saas` had not deployed
   * it when this was wired, so today it is simply absent (UC-049 E1). Drop the
   * `?` once the deploy is confirmed. The mapper below does not rely on this
   * type either way — nothing runtime-validates the response.
   */
  IsSuperAdmin?: boolean
  Permissions: Record<string, number> | null
  Roles: number[] | null
  /** The caller's own roles with their grants (UC-084.9); not sent yet. */
  RoleDetails?: RoleDetailResponse[] | null
  ViewCurrencyId: number | null
  PaymentApprovalLevel: number | null
}

interface RoleDetailResponse {
  Id: number
  Name: string
  IsPreset: boolean
  Modules: OwnRoleModule[] | null
}

/** The codes th_saas uses when it refuses the identity itself, not the token (UC-084.1). */
export type IdentityRefusalCode = 'InvitationNotValid'

/**
 * `GET /api/UserIdentity` (or `/api/Users/tenants`) answered 401 with a body
 * naming why the person is refused: on a first login, the middleware found no
 * usable invitation (expired, revoked, consumed or unknown). Distinct from a
 * plain 401, which stays an ordinary login problem.
 */
export class IdentityRefusalError extends Error {
  constructor(public readonly code: IdentityRefusalCode) {
    super(`Identity refused: ${code}`)
    this.name = 'IdentityRefusalError'
  }
}

/**
 * The bootstrap identity calls answered 403 `UserAccessSuspended` (UC-084.3 E2):
 * the membership in the selected tenant is suspended or deactivated, the session
 * itself is fine. These calls skip the API client, so they raise it themselves.
 */
export class AccessSuspendedError extends Error {
  constructor() {
    super('Access suspended in the selected tenant')
    this.name = 'AccessSuspendedError'
  }
}

// The middleware writes the key in camelCase (`code`), unlike every other new
// th_saas payload; `Code` is accepted too in case that ever changes. Anything
// that is not a JSON object with a string code reads as "no code".
async function readRefusalCode(response: Response): Promise<string | null> {
  try {
    const body = await response.json() as unknown
    if (typeof body !== 'object' || body === null || Array.isArray(body)) return null
    const record = body as Record<string, unknown>
    const code = record.code ?? record.Code
    return typeof code === 'string' ? code : null
  } catch {
    return null
  }
}

async function throwOnFailure(response: Response, requestName: string): Promise<void> {
  if (response.ok) return
  if (response.status === 401 || response.status === 403) {
    const code = await readRefusalCode(response)
    if (response.status === 401 && code === 'InvitationNotValid') {
      throw new IdentityRefusalError('InvitationNotValid')
    }
    if (response.status === 403 && code === ACCESS_SUSPENDED_CODE) throw new AccessSuspendedError()
  }
  throw new Error(`${requestName} request failed: ${response.status}`)
}

function deriveNamesFromEmail(email: string): { firstname: string; lastname: string } {
  const local = email.split('@')[0] ?? ''
  const parts = local.split(/[._-]/)
  if (parts.length >= 2) {
    return {
      firstname: parts[0]!.charAt(0).toUpperCase() + parts[0]!.slice(1),
      lastname: parts[1]!.charAt(0).toUpperCase() + parts[1]!.slice(1),
    }
  }
  return { firstname: local.charAt(0).toUpperCase() + local.slice(1), lastname: '' }
}

export async function fetchUserIdentity(token: string): Promise<AuthUser> {
  const response = await fetch(coreUrl('/api/UserIdentity/'), {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  await throwOnFailure(response, 'UserIdentity')

  const data = await response.json() as UserIdentityResponse
  const email = data.Email ?? ''

  let firstname = data.Firstname ?? ''
  let lastname = data.Lastname ?? ''
  if (!firstname && !lastname && email) {
    const derived = deriveNamesFromEmail(email)
    firstname = derived.firstname
    lastname = derived.lastname
  }

  return {
    userId: String(data.UserId),
    entityId: data.TenantId,
    username: data.Username ?? email,
    firstname,
    lastname,
    email,
    organizationId: data.TenantId,
    // Strict `=== true`, not `?? false`: the response is not runtime-validated,
    // and `canAccess` gates on truthiness, so a non-boolean like the string
    // 'False' would grant the full permission bypass. Same guard the B2C
    // provider already applies to its (never emitted) claim.
    isSuperAdmin: data.IsSuperAdmin === true,
    isAdmin: data.IsAdmin,
    permissions: data.Permissions ?? {},
    roles: (data.Roles ?? []).map(String),
    roleDetails: data.RoleDetails?.map((role) => ({
      id: role.Id, name: role.Name, isPreset: role.IsPreset, modules: role.Modules ?? [],
    })),
    viewCurrencyId: data.ViewCurrencyId ?? 0,
    paymentApprovalLevel: data.PaymentApprovalLevel ?? 0,
    tenants: [],
  }
}

interface UserTenantResponse {
  TenantId: number
  UserId: number
  TenantName: string
  UserName: string
  IsSelected: boolean
}

export async function fetchUserTenants(token: string): Promise<Array<{ id: number; name: string }>> {
  const response = await fetch(coreUrl('/api/Users/tenants'), {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  await throwOnFailure(response, 'UserTenants')

  const data = await response.json() as UserTenantResponse[]
  return data.map((tenant) => ({ id: tenant.TenantId, name: tenant.TenantName }))
}

export async function switchTenant(token: string, tenantId: number): Promise<void> {
  const response = await fetch(coreUrl(`/api/Users/switchTenant/${tenantId}`), {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Switch tenant failed: ${response.status}`)
  }
}
